/**
 * The dashboard list: folders and the dashboards inside them.
 *
 * This is the section's landing page, the way Grafana's is - you arrive at a
 * list and open something, rather than being dropped into whichever dashboard
 * happened to be first.
 *
 * Folders are flat: a folder holds dashboards and never other folders. Search
 * flattens the tree entirely and lists matches with the folder they came from,
 * because once you are searching, the hierarchy is not what you are navigating
 * by.
 *
 * Right-clicking a row opens the same menu the catalog's cards have - Share,
 * Rename, Move, Delete - because sharing a dashboard is the same act as sharing
 * a chart, and the gesture people try first is the one they already know.
 *
 * A dashboard is filed by dragging it onto a folder, or out onto the list to
 * take it back to the top level - the gesture people already expect from a file
 * manager, and it says where the thing will land while you are still holding
 * it. The same move is on the row's ⇄ menu, because a drag is unavailable to
 * anyone working from the keyboard.
 *
 * **Both destinations are drawn while something is in hand.** A folder marks
 * its whole zone - the header and everything indented under it - not the one
 * line the cursor is over, because what is being offered is "in here". And a
 * dashboard that is *inside* a folder gets a band at the top of the list to
 * drop onto: the top level is a real destination, and before the band existed
 * the only way to reach it was to find a few pixels of list padding between two
 * folders.
 */

import { html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LightElement } from '../../lit-base.ts';
import { showToast } from '../../ui/toast.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import './dash-dropdown.ts';
import type { DashDropdown } from './dash-dropdown.ts';
import { StoreController } from '../../state/controller.ts';
import { timeAgo } from '../../utils/dates.ts';
import { userMessage } from '../../api/errors.ts';
import { DashboardConflictError } from './dashboard-repository.ts';
import { openDashboardShareDialog } from './dashboard-share-state.ts';
import {
  browseQuery,
  canEditDashboard,
  createDashboard,
  createFolder,
  dashboards,
  dashboardsLoaded,
  deleteDashboard,
  deleteFolder,
  expandedFolders,
  folders,
  isSharedWithMe,
  moveDashboard,
  openDashboard,
  renameDashboard,
  renameFolder,
  sharedFolderOpen,
  toggleFolder,
  type Dashboard,
  type DashboardFolder,
  chartTiles,
} from './dashboard-state.ts';

/**
 * Small stroke icons rather than font glyphs: `▣` looked like an accidental
 * checkbox in the folder row, and its shape changed with the platform font.
 * These follow the same currentColor / rounded-stroke treatment as the open
 * dashboard toolbar icons.
 */
const DASHBOARD_GLYPH = svg`<svg
  class="dbrowse-icon"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <rect
    x="4"
    y="3.5"
    width="16"
    height="17"
    rx="1"
    stroke="currentColor"
    stroke-width="1.6"
  />
  <path
    d="M8 8h8M8 12h8M8 16h5"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
  />
</svg>`;

const FOLDER_GLYPH = svg`<svg
  class="dbrowse-icon dbrowse-folder-icon"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <path
    d="M3.5 6.5h6l2 2h9v9.25a1.75 1.75 0 0 1-1.75 1.75H5.25a1.75 1.75 0 0 1-1.75-1.75V6.5z"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
  <path
    d="M3.5 9h17"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
  />
</svg>`;

/** Two people: the built-in folder that holds what others shared with you. */
const SHARED_GLYPH = svg`<svg
  class="dbrowse-icon dbrowse-folder-icon"
  width="16"
  height="16"
  viewBox="0 0 24 24"
  fill="none"
  aria-hidden="true"
>
  <circle cx="9" cy="8.5" r="3.25" stroke="currentColor" stroke-width="1.6" />
  <path
    d="M3 19.5c.6-3.3 3-5.25 6-5.25s5.4 1.95 6 5.25"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
  />
  <path
    d="M15.5 5.6a3.25 3.25 0 0 1 0 5.8M17.6 14.6c1.8.7 3 2.4 3.4 4.9"
    stroke="currentColor"
    stroke-width="1.6"
    stroke-linecap="round"
  />
</svg>`;

/** A bar chart, for the row's chart-count chip. */
const CHART_GLYPH = svg`<svg class="dbrowse-chip-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
  <path
    d="M4 20h16M7 16v-5M12 16V6M17 16v-8"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
  />
</svg>`;

/**
 * Tiles across every tab - every tile is a chart, so the row's chart count. A
 * partial row has no `tabs` to count, so it says what the server counted.
 */
function tileCount(d: Dashboard): number {
  if (d.partial && d.counts) return d.counts.tiles;
  return d.tabs.reduce((n, t) => n + chartTiles(t).length, 0);
}

function tabCount(d: Dashboard): number {
  return d.partial && d.counts ? d.counts.tabs : d.tabs.length;
}

function matches(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

/**
 * What to say when a write was refused.
 *
 * A conflict is the one worth naming: it is not a failure, it is somebody else
 * having got there first, and the answer is to reload rather than to retry.
 */
function reason(error: Error, fallback: string): string {
  if (error instanceof DashboardConflictError) {
    return 'Somebody else changed this. Reload to see their version.';
  }
  // The server's own sentence when it wrote one for people ("Only whoever
  // made that folder ..."), with its reference on a fault; never a raw error.
  return userMessage(error, fallback);
}

@customElement('dashboard-browse')
export class DashboardBrowse extends LightElement {
  private store = new StoreController(this, [
    dashboards,
    folders,
    browseQuery,
    expandedFolders,
    sharedFolderOpen,
    dashboardsLoaded,
  ]);

  /** Row currently being renamed, and a pending delete awaiting confirmation. */
  @state() private renamingId: string | null = null;
  @state() private pendingDeleteId: string | null = null;

  /**
   * The dashboard being dragged, and where it would land.
   *
   * `dropFolder` is deliberately three-valued: a folder id, `null` for the top
   * level, and `undefined` for "nothing is a target right now" - the top level
   * is a real destination, not the absence of one.
   */
  /** Which row's right-click menu is open, and where, in viewport coordinates. */
  @state() private menuFor: string | null = null;
  @state() private menuAt: { x: number; y: number } | null = null;

  @state() private dragId: string | null = null;
  @state() private dropFolder: string | null | undefined = undefined;
  private expandTimer: number | null = null;

  /* ── Drag and drop ────────────────────────────────────── */

  private onDragStart(e: DragEvent, d: Dashboard): void {
    this.dragId = d.id;
    // The id also travels on the event, so a drop still knows what it received
    // if this component re-rendered in between.
    e.dataTransfer?.setData('text/plain', d.id);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
  }

  private endDrag(): void {
    this.dragId = null;
    this.dropFolder = undefined;
    this.clearExpandTimer();
  }

  private dragOver(e: DragEvent, folderId: string | null): void {
    if (!this.dragId) return;
    // Both are required for a drop to be allowed at all, and stopping the event
    // is what keeps a folder's own target from being taken over by the list
    // behind it.
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    if (this.dropFolder === folderId) return;
    this.dropFolder = folderId;
    this.armExpand(folderId);
  }

  /** Holding over a closed folder opens it, so you can see what is in there. */
  private armExpand(folderId: string | null): void {
    this.clearExpandTimer();
    if (folderId === null || expandedFolders.value.has(folderId)) return;
    this.expandTimer = window.setTimeout(() => {
      this.expandTimer = null;
      if (this.dropFolder === folderId) toggleFolder(folderId);
    }, 650);
  }

  private clearExpandTimer(): void {
    if (this.expandTimer !== null) clearTimeout(this.expandTimer);
    this.expandTimer = null;
  }

  private drop(e: DragEvent, folderId: string | null): void {
    e.preventDefault();
    e.stopPropagation();
    const id = this.dragId ?? e.dataTransfer?.getData('text/plain') ?? '';
    if (id) void this.move(id, folderId);
    this.endDrag();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.clearExpandTimer();
    this.closeMenu();
  }

  /* ── The row menu ─────────────────────────────────────── */

  /**
   * Open the menu at the cursor, clamped so it never opens off-screen - the
   * last row of a long list is exactly where a menu that opens down-and-right
   * would fall out of the viewport.
   */
  private openRowMenu(e: MouseEvent, d: Dashboard): void {
    e.preventDefault();
    e.stopPropagation();
    const width = 200;
    const height = canEditDashboard(d) ? 230 : 110;
    this.menuFor = d.id;
    this.menuAt = {
      x: Math.min(e.clientX, window.innerWidth - width - 8),
      y: Math.min(e.clientY, window.innerHeight - height - 8),
    };
    document.addEventListener('click', this.closeMenu, { once: true });
    document.addEventListener('keydown', this.onMenuKey);
  }

  private closeMenu = (): void => {
    this.menuFor = null;
    this.menuAt = null;
    document.removeEventListener('keydown', this.onMenuKey);
  };

  private onMenuKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this.closeMenu();
  };

  /** The link a dashboard is shared by - what the open dashboard's URL becomes. */
  private async copyLink(d: Dashboard): Promise<void> {
    const url = `${window.location.origin}/?section=dashboards&db=${d.id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast('success', 'Link copied');
    } catch {
      showToast('error', `Copy failed - the link is ${url}`);
    }
  }

  /* ── Actions ──────────────────────────────────────────── */

  private async onNewDashboard(folderId: string | null = null): Promise<void> {
    const d = await createDashboard('New dashboard', folderId);
    if (!d) {
      showToast('error', 'Could not create the dashboard.');
      return;
    }
    // Straight into the new dashboard: it is empty, and the next thing anyone
    // wants is to put a chart in it.
    openDashboard(d.id);
  }

  private async onNewFolder(): Promise<void> {
    const folder = await createFolder('New folder');
    if (!folder) {
      showToast('error', 'Could not create the folder.');
      return;
    }
    await this.startRename(folder.id);
  }

  private async commitRename(id: string, value: string, isFolder: boolean): Promise<void> {
    if (this.renamingId !== id) return;
    this.renamingId = null;
    const result = isFolder ? await renameFolder(id, value) : await renameDashboard(id, value);
    // The name on screen has already gone back to what it was; saying why is
    // the rest of that, otherwise a rename that did not take looks like a
    // keystroke that was missed.
    if (result !== true) showToast('error', reason(result, 'Could not rename that.'));
  }

  private async startRename(id: string): Promise<void> {
    this.renamingId = id;
    await this.updateComplete;
    const input = this.querySelector<HTMLInputElement>('.dbrowse-rename');
    input?.focus();
    input?.select();
  }

  /** Move a dashboard, and say so when the server would not have it. */
  private async move(id: string, folderId: string | null): Promise<void> {
    const result = await moveDashboard(id, folderId);
    if (result !== true) showToast('error', reason(result, 'Could not move that.'));
  }

  private async removeRow(id: string, isFolder: boolean): Promise<void> {
    const result = isFolder ? await deleteFolder(id) : await deleteDashboard(id);
    if (result !== true) showToast('error', reason(result, 'Could not delete that.'));
  }

  /**
   * Delete from the menu asks, where the row's own ✕ arms itself instead.
   *
   * The button can be clicked again to undo the arming; a menu closes the
   * moment it is used, so there is nothing left to take back - the question has
   * to be asked while it is still a question.
   */
  private async confirmDelete(d: Dashboard): Promise<void> {
    const ok = await confirmAction({
      title: `Delete "${d.name}"?`,
      message: 'The dashboard goes out of the list. Its tiles go with it.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (ok) await this.removeRow(d.id, false);
  }

  private armDelete(id: string): void {
    this.pendingDeleteId = id;
    setTimeout(() => {
      if (this.pendingDeleteId === id) this.pendingDeleteId = null;
    }, 4000);
  }

  /* ── Row renderers ────────────────────────────────────── */

  private renderRename(
    id: string,
    value: string,
    isFolder: boolean,
  ): TemplateResult {
    return html`<input
      class="dbrowse-rename"
      .value=${value}
      autofocus
      @click=${(e: Event) => e.stopPropagation()}
      @blur=${(e: Event) => this.commitRename(id, (e.target as HTMLInputElement).value, isFolder)}
      @keydown=${(e: KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') this.renamingId = null;
      }}
    />`;
  }

  private renderDashboardRow(d: Dashboard, showFolder = false): TemplateResult {
    const shared = isSharedWithMe(d);
    // A shared dashboard's folder is its owner's filing, not yours: it is
    // listed under "Shared with me" and moving it would move it for the owner.
    const folder = shared ? null : folders.value.find((f) => f.id === d.folderId);
    const armed = this.pendingDeleteId === d.id;
    const editable = canEditDashboard(d);
    const movable = editable && !shared;
    const charts = tileCount(d);
    const tabs = tabCount(d);
    return html`
      <div
        class="dbrowse-row dbrowse-dashboard ${this.dragId === d.id ? 'is-dragging' : ''}"
        role="button"
        tabindex="0"
        draggable=${movable ? 'true' : 'false'}
        @dragstart=${(e: DragEvent) => this.onDragStart(e, d)}
        @dragend=${() => this.endDrag()}
        @contextmenu=${(e: MouseEvent) => this.openRowMenu(e, d)}
        @click=${() => openDashboard(d.id)}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') openDashboard(d.id);
        }}
      >
        ${movable
          ? html`<span class="dbrowse-grip" aria-hidden="true" title="Drag to a folder">⠿</span>`
          : html`<span class="dbrowse-grip" aria-hidden="true"></span>`}
        ${DASHBOARD_GLYPH}
        <span class="dbrowse-name">
          ${this.renamingId === d.id
            ? this.renderRename(d.id, d.name, false)
            : html`<span class="dbrowse-title-line">
                <span class="dbrowse-label">${d.name}</span>
                ${editable
                  ? html`<button
                      class="dbrowse-title-edit"
                      title="Rename dashboard"
                      aria-label=${`Rename ${d.name}`}
                      @keydown=${(e: KeyboardEvent) => e.stopPropagation()}
                      @click=${(e: Event) => {
                        e.stopPropagation();
                        void this.startRename(d.id);
                      }}
                    >✎</button>`
                  : nothing}
              </span>`}
          <span class="dbrowse-meta">
            ${tabs} ${tabs === 1 ? 'tab' : 'tabs'}
            ${showFolder && folder ? html` · in ${folder.name}` : nothing}
            ${showFolder && shared ? html` · shared with you` : nothing}
          </span>
        </span>
        <span class="dbrowse-chips">
          <span
            class="dbrowse-badge"
            title=${`${charts} ${charts === 1 ? 'chart' : 'charts'} on this dashboard`}
            >${CHART_GLYPH} ${charts}</span
          >
          ${this.renderShareBadge(d)}
        </span>
        <span class="dbrowse-updated">${timeAgo(d.updatedAt)}</span>
        <span class="dbrowse-actions" @click=${(e: Event) => e.stopPropagation()}>
          ${editable
            ? html`${movable ? this.renderMoveMenu(d) : nothing}
              <button
                class="dbrowse-btn ${armed ? 'is-danger' : ''}"
                title=${armed ? 'Click again to delete' : 'Delete'}
                @click=${() => (armed ? void this.removeRow(d.id, false) : this.armDelete(d.id))}
              >
                ${armed ? 'Delete?' : '✕'}
              </button>`
            : nothing}
        </span>
      </div>
    `;
  }

  /**
   * How this row reached the viewer, in the one chip it is worth saying.
   *
   * On your own dashboard: how many people you have shared it with, and nothing
   * at all until you have. On one shared with you: whose it is and what you may
   * do with it - the two questions you have about a row you did not make.
   */
  private renderShareBadge(d: Dashboard): TemplateResult | typeof nothing {
    const access = d.access;
    if (!access) return nothing;
    if (access.isOwner) {
      const count = access.shareCount ?? 0;
      return count
        ? html`<span
            class="dbrowse-badge"
            title=${`Shared with ${count} ${count === 1 ? 'person or group' : 'people and groups'}`}
            >Shared · ${count}</span
          >`
        : nothing;
    }
    const owner = access.ownerName ?? 'Someone else';
    return html`<span class="dbrowse-badge is-shared" title=${`${owner} shared this with you`}>
      <span class="dbrowse-badge-owner">${owner}</span>
      <span class="dbrowse-badge-role">${access.canEdit ? 'Editor' : 'View only'}</span>
    </span>`;
  }

  /** The right-click menu: the catalog card's, over a dashboard row. */
  private renderRowMenu(): TemplateResult | typeof nothing {
    const d = dashboards.value.find((x) => x.id === this.menuFor);
    const at = this.menuAt;
    if (!d || !at) return nothing;
    const editable = canEditDashboard(d);
    // Only a stored dashboard can be shared: `access` is the server's answer
    // about this row, and a browser-stored one has none - there is nobody to
    // share it with, and its id means nothing to the API.
    const shareable = editable && !!d.access;
    return html`<div
      class="cat-menu"
      role="menu"
      style=${`left:${at.x}px; top:${at.y}px`}
      @click=${(e: Event) => e.stopPropagation()}
      @contextmenu=${(e: Event) => e.preventDefault()}
    >
      <button class="cat-menu-item" role="menuitem" @click=${() => {
        this.closeMenu();
        openDashboard(d.id);
      }}>
        <span class="cat-menu-icon" aria-hidden="true">▸</span> Open
      </button>
      <button class="cat-menu-item" role="menuitem" @click=${() => {
        this.closeMenu();
        void this.copyLink(d);
      }}>
        <span class="cat-menu-icon" aria-hidden="true">🔗</span> Copy link
      </button>
      ${shareable
        ? html`
            <div class="cat-menu-sep"></div>
            <button class="cat-menu-item" role="menuitem" @click=${() => {
              this.closeMenu();
              void openDashboardShareDialog(d.id);
            }}>
              <span class="cat-menu-icon" aria-hidden="true">◈</span> Share…
            </button>
          `
        : nothing}
      ${editable
        ? html`
            ${shareable ? nothing : html`<div class="cat-menu-sep"></div>`}
            <button class="cat-menu-item" role="menuitem" @click=${() => {
              this.closeMenu();
              void this.startRename(d.id);
            }}>
              <span class="cat-menu-icon" aria-hidden="true">✎</span> Rename
            </button>
            <div class="cat-menu-sep"></div>
            <button
              class="cat-menu-item is-destructive"
              role="menuitem"
              @click=${() => {
                this.closeMenu();
                void this.confirmDelete(d);
              }}
            >
              <span class="cat-menu-icon" aria-hidden="true">×</span> Delete
            </button>
          `
        : nothing}
    </div>`;
  }

  /**
   * The same move as the drag, for the keyboard - and for anyone who would
   * rather pick than aim. A menu rather than a `<select>`: the select read as a
   * form field sitting in a list of names, and it announced itself on every row
   * whether or not there were folders to move into.
   */
  private renderMoveMenu(d: Dashboard): TemplateResult | typeof nothing {
    if (!folders.value.length) return nothing;
    const pick = (e: Event, folderId: string | null): void => {
      (e.currentTarget as HTMLElement).closest<DashDropdown>('dash-dropdown')?.close();
      void this.move(d.id, folderId);
    };
    return html`<dash-dropdown
      triggerTitle="Move to folder"
      triggerClass="dbrowse-btn"
      panelClass="is-menu"
      panelRole="menu"
      alignEnd
      fixed
      .triggerContent=${html`<span aria-hidden="true">⇄</span>`}
      .panel=${() => html`
        <button
          class="dash-menu-item ${d.folderId === null ? 'is-current' : ''}"
          role="menuitem"
          @click=${(e: Event) => pick(e, null)}
        >
          <span class="dash-menu-label">Dashboards</span>
          <span class="dash-menu-hint">Top level, outside any folder</span>
        </button>
        ${folders.value.map(
          (f) => html`<button
            class="dash-menu-item ${d.folderId === f.id ? 'is-current' : ''}"
            role="menuitem"
            @click=${(e: Event) => pick(e, f.id)}
          >
            <span class="dash-menu-label">${f.name}</span>
          </button>`,
        )}
      `}
    ></dash-dropdown>`;
  }

  /**
   * "Take it out of the folder", as a place to drop it.
   *
   * Shown while a dashboard that lives in a folder is in hand: for one already
   * at the top level it would offer the move it is not making, and for a folder
   * drag it means nothing. It is the same destination the list itself is, said
   * where it can actually be hit.
   *
   * **It is always in the DOM, and only its classes change.** Rendering it at
   * `dragstart` - the obvious way - made the bug it was meant to fix worse:
   * Chrome cancels a drag outright (dragstart, then dragend with dropEffect
   * "none", before the pointer has moved) when the list relays out underneath
   * it, and this band appeared for exactly one kind of drag: a dashboard coming
   * out of a folder. It is laid over the search field rather than inserted
   * above the list, so showing it moves nothing either.
   */
  private renderRootZone(): TemplateResult {
    const dragged = dashboards.value.find((d) => d.id === this.dragId);
    const armed = !!dragged && dragged.folderId !== null;
    const targeted = armed && this.dropFolder === null;
    return html`<div
      class="dbrowse-rootzone ${armed ? 'is-armed' : ''} ${targeted ? 'is-drop' : ''}"
      aria-hidden=${armed ? 'false' : 'true'}
      @dragover=${(e: DragEvent) => this.dragOver(e, null)}
      @drop=${(e: DragEvent) => this.drop(e, null)}
    >
      <span class="dbrowse-rootzone-icon" aria-hidden="true">↥</span>
      Drop here to take it out of the folder
    </div>`;
  }

  private renderFolderRow(f: DashboardFolder): TemplateResult {
    const open = expandedFolders.value.has(f.id);
    const inside = dashboards.value.filter((d) => d.folderId === f.id && !isSharedWithMe(d));
    const armed = this.pendingDeleteId === f.id;
    const targeted = this.dropFolder === f.id;
    return html`
      <div
        class="dbrowse-group ${open ? 'is-open' : ''} ${targeted ? 'is-drop' : ''}"
        @dragover=${(e: DragEvent) => this.dragOver(e, f.id)}
        @drop=${(e: DragEvent) => this.drop(e, f.id)}
      >
        <div
          class="dbrowse-row dbrowse-folder"
          role="button"
          tabindex="0"
          aria-expanded=${open}
          @click=${() => toggleFolder(f.id)}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') toggleFolder(f.id);
          }}
        >
          <span class="dbrowse-chevron ${open ? 'is-open' : ''}" aria-hidden="true"></span>
          ${FOLDER_GLYPH}
          <span class="dbrowse-name">
            ${this.renamingId === f.id
              ? this.renderRename(f.id, f.name, true)
              : html`<span class="dbrowse-title-line">
                  <span class="dbrowse-label">${f.name}</span>
                  <button
                    class="dbrowse-title-edit"
                    title="Rename folder"
                    aria-label=${`Rename ${f.name}`}
                    @keydown=${(e: KeyboardEvent) => e.stopPropagation()}
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      void this.startRename(f.id);
                    }}
                  >✎</button>
                </span>`}
          </span>
          <span class="dbrowse-count"
            >${inside.length} ${inside.length === 1 ? 'dashboard' : 'dashboards'}</span
          >
          <span class="dbrowse-actions" @click=${(e: Event) => e.stopPropagation()}>
            <button class="dbrowse-btn" title="New dashboard in this folder" @click=${() =>
              this.onNewDashboard(f.id)}>
              +
            </button>
            <button
              class="dbrowse-btn ${armed ? 'is-danger' : ''}"
              title=${armed
                ? 'Click again to delete the folder (dashboards are kept)'
                : 'Delete folder'}
              @click=${() => (armed ? void this.removeRow(f.id, true) : this.armDelete(f.id))}
            >
              ${armed ? 'Delete?' : '✕'}
            </button>
          </span>
        </div>
        ${open
          ? html`<div class="dbrowse-children">
              ${inside.length
                ? inside.map((d) => this.renderDashboardRow(d))
                : html`<div class="dbrowse-row dbrowse-empty-folder">
                    <span class="dbrowse-meta">
                      ${this.dragId ? 'Drop here to file it in this folder' : 'Empty folder'}
                    </span>
                  </div>`}
            </div>`
          : nothing}
      </div>
    `;
  }

  /**
   * "Shared with me": a folder nobody made, holding every dashboard someone
   * else shared with you, pinned above your own.
   *
   * It opens and closes like a folder and does nothing else - no rename, no
   * delete, no New dashboard, and it is not a place to drop anything. Its rows
   * cannot be dragged out either: their folder is the owner's to choose.
   * Dragging over it swallows the event, so the list behind does not offer
   * "top level" through it.
   */
  private renderSharedFolder(shared: Dashboard[]): TemplateResult {
    const open = sharedFolderOpen.value;
    const toggle = (): void => sharedFolderOpen.set(!open);
    return html`
      <div
        class="dbrowse-group dbrowse-shared ${open ? 'is-open' : ''}"
        @dragover=${(e: DragEvent) => {
          if (!this.dragId) return;
          e.stopPropagation();
          if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
          this.dropFolder = undefined;
          this.clearExpandTimer();
        }}
        @drop=${(e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          this.endDrag();
        }}
      >
        <div
          class="dbrowse-row dbrowse-folder"
          role="button"
          tabindex="0"
          aria-expanded=${open}
          title="Dashboards other people shared with you. Their owners decide where they are filed."
          @click=${toggle}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') toggle();
          }}
        >
          <span class="dbrowse-chevron ${open ? 'is-open' : ''}" aria-hidden="true"></span>
          ${SHARED_GLYPH}
          <span class="dbrowse-name">
            <span class="dbrowse-label">Shared with me</span>
          </span>
          <span class="dbrowse-count"
            >${shared.length} ${shared.length === 1 ? 'dashboard' : 'dashboards'}</span
          >
          <span class="dbrowse-actions"></span>
        </div>
        ${open
          ? html`<div class="dbrowse-children">
              ${shared.map((d) => this.renderDashboardRow(d))}
            </div>`
          : nothing}
      </div>
    `;
  }

  /* ── Render ───────────────────────────────────────────── */

  private renderResults(): TemplateResult | TemplateResult[] {
    const query = browseQuery.value.trim();
    const all = dashboards.value;

    if (query) {
      const hits = all.filter(
        (d) =>
          matches(d.name, query) ||
          matches(folders.value.find((f) => f.id === d.folderId)?.name ?? '', query),
      );
      if (!hits.length) {
        return html`<div class="dbrowse-blank">
          <p>Nothing matches “${query}”.</p>
        </div>`;
      }
      return hits.map((d) => this.renderDashboardRow(d, true));
    }

    const shared = all.filter(isSharedWithMe);
    const rootDashboards = all.filter((d) => d.folderId === null && !isSharedWithMe(d));
    if (!all.length && !folders.value.length) {
      return html`<div class="dbrowse-blank">
        <h2>No dashboards yet</h2>
        <p>
          A dashboard is tabs of charts you arrange yourself. Create a blank one, then use
          <strong>Add chart</strong> to build panels into it.
        </p>
        <button class="dash-btn is-primary" @click=${() => this.onNewDashboard()}>
          New dashboard
        </button>
      </div>`;
    }

    return [
      ...(shared.length ? [this.renderSharedFolder(shared)] : []),
      ...folders.value.map((f) => this.renderFolderRow(f)),
      ...rootDashboards.map((d) => this.renderDashboardRow(d)),
    ];
  }

  override render(): TemplateResult {
    return html`
      <div class="dbrowse">
        <div class="dbrowse-head">
          <h1>Dashboards</h1>
          <div class="dbrowse-head-actions">
            <button class="dash-btn" @click=${this.onNewFolder}>New folder</button>
            <button class="dash-btn is-primary" data-tour="dash-new" @click=${() => this.onNewDashboard()}>
              New dashboard
            </button>
          </div>
        </div>

        <input
          class="dbrowse-search"
          type="search"
          placeholder="Search dashboards and folders"
          .value=${browseQuery.value}
          @input=${(e: Event) => browseQuery.set((e.target as HTMLInputElement).value)}
        />

        <div
          class="dbrowse-list ${this.dragId ? 'is-dragging' : ''} ${
            this.dragId && this.dropFolder === null ? 'is-drop' : ''
          }"
          @dragover=${(e: DragEvent) => this.dragOver(e, null)}
          @drop=${(e: DragEvent) => this.drop(e, null)}
        >
          ${this.renderRootZone()} ${this.renderResults()}
        </div>
        ${this.renderRowMenu()}
      </div>
    `;
  }
}
